import { Assets, Spritesheet, Texture, ISpritesheetData } from 'pixi.js';
import { GRID_SHEETS } from '../data/spritesheets/generated/grid-sheets.manifest';
import { GridSheetDescriptor } from '../types/sprite.types';
import { useSpritesStore } from '../store/spritesSlice';

interface RowEntry {
  descriptor: GridSheetDescriptor;
}

const registry: Map<string, RowEntry> = new Map();
for (const descriptor of GRID_SHEETS) {
  for (let row = 0; row < descriptor.rows; row++) {
    registry.set(`${descriptor.id}-${row}`, { descriptor });
  }
}

const parsing = new Map<string, Promise<void>>();

function buildSheetData(descriptor: GridSheetDescriptor): ISpritesheetData {
  const { id, cell, cols, rows } = descriptor;
  const frames: ISpritesheetData['frames'] = {};
  const animations: ISpritesheetData['animations'] = {};

  for (let r = 0; r < rows; r++) {
    const rowFrames: string[] = [];
    for (let c = 0; c < cols; c++) {
      const frameName = `${id}-r${r}c${c}`;
      frames[frameName] = {
        frame: { x: c * cell, y: r * cell, w: cell, h: cell },
        sourceSize: { w: cell, h: cell },
        spriteSourceSize: { x: 0, y: 0, w: cell, h: cell },
      };
      rowFrames.push(frameName);
    }
    animations[`${id}-${r}`] = rowFrames;
  }

  return {
    frames,
    meta: {
      image: descriptor.image,
      size: { w: cols * cell, h: rows * cell },
      scale: '1',
    },
    animations,
  };
}

/**
 * Lazily parses the grid sheet that owns `animName` and registers it in the
 * sprite store.
 *
 * @returns a promise that resolves once the sheet is in the store AND its base
 * image is fully loaded, or `null` when nothing must be awaited (`animName` is
 * not a grid sheet, or the sheet is already loaded). Callers defer using the
 * texture until the promise resolves — registering an animation before its
 * texture lands lets it expire (short duration) before rendering ("spell VFX
 * missing on first cast"), and rendering over a still-loading BaseTexture can
 * crash PIXI when the sprite unmounts mid-load.
 *
 * EntityDisplay resolves an animation via `textures[X].animations[X]`, so the
 * single parsed Spritesheet is stored under EVERY row key it contains — that is
 * why all `${id}-${r}` map to the same sheet instance.
 *
 * Idempotent: returns `null` when already in the store, and concurrent calls for
 * the same sheet share the one in-flight parse promise.
 */
export function ensureGridSheet(animName: string): Promise<void> | null {
  const entry = registry.get(animName);
  if (!entry) {
    return null;
  }
  if (useSpritesStore.getState().textures[animName]) {
    return null;
  }
  const sheetId = entry.descriptor.id;
  const inFlight = parsing.get(sheetId);
  if (inFlight) {
    return inFlight;
  }

  const descriptor = entry.descriptor;
  const data = buildSheetData(descriptor);

  // Load the base image to completion BEFORE parsing/registering the sheet.
  // `Spritesheet.parse()` resolves as soon as the frame rectangles are computed,
  // NOT when the image is decoded — so registering early lets a short-lived VFX
  // or projectile mount an AnimatedSprite over a still-loading BaseTexture. If
  // that sprite unmounts before the load lands (common on a client receiving
  // cold combat VFX), PIXI fires `_onTextureUpdate` on the destroyed sprite
  // (transform null) and crashes. `Assets.load` is URL-cache-keyed, so the warm
  // host path is unchanged; this just guarantees a valid texture before render.
  const promise = Assets.load<Texture>(descriptor.image)
    .then(async (texture) => {
      const sheet = new Spritesheet(texture, data);
      await sheet.parse();
      const current = useSpritesStore.getState().textures;
      const next: { [key: string]: Spritesheet } = { ...current };
      for (let r = 0; r < descriptor.rows; r++) {
        next[`${descriptor.id}-${r}`] = sheet;
      }
      useSpritesStore.getState().setTextures(next);
    })
    .finally(() => {
      parsing.delete(sheetId);
    });
  parsing.set(sheetId, promise);
  return promise;
}

/**
 * Eagerly warm the (few, tiny) bullet/projectile grid sheets. A replicated
 * projectile renders immediately on spawn, so without a warm sheet its first
 * appearance flashes a placeholder while the texture loads cold — most visible
 * on a multiplayer client, which never fires locally to warm them.
 */
export function preloadBulletSheets(): void {
  for (const descriptor of GRID_SHEETS) {
    if (descriptor.image.includes('/bullets/')) {
      void ensureGridSheet(`${descriptor.id}-0`);
    }
  }
}

export function isGridSheetAnim(animName: string): boolean {
  return registry.has(animName);
}

export function getGridSheetDescriptor(
  animName: string,
): GridSheetDescriptor | undefined {
  return registry.get(animName)?.descriptor;
}
